import type { datetime, Modify, OPTIMIZATION_TYPE, Task } from '@bringg/types';

export type RouteTasks = {
	task_id: number;
	way_point_id: number;
};

export type DriverRoute = {
	driver_id: number;
	trailer_id: number;
	tasks: RouteTasks[];
	run_id?: number;
	vehicle_id: number;
	route_idx?: number;
};

export type EtaRequest = {
	optimization_type: OPTIMIZATION_TYPE;
	team_id: number;
	driver_ids: number[];
	task_ids: number[];
	routes: DriverRoute[];
	request_uuid?: string;
	scheduled_date: string; // only date 'YYYY-MM-DD'
	preserve_current_runs: boolean;
	run_id?: number;
};

export type EtaCalculationResultsData = {
	updatedRunsTasks: EtaResponseTaskItem[];
	updatedRunsBreaks: EtaResponseBreakItem[];
	updatedRunsDirections: EtaResponseDirectionsItem[];
	updatedRoutesInfo: EtaResponseRouteItem[];
};
export type EtaCalculationUpdateResults = EtaCalculationResultsData & {
	requestRunId?: number;
};

export type FullEtaRequest = EtaRequest & {
	merchant_id: number;
	user_id: number;
	request_uuid: string;
	optimize_for: 'custom';
	window_start: datetime;
	window_end: datetime;
	total_run_ids: number[];
	min_start_time_ms: number;
	allow_past_calculation: boolean;
	apply?: boolean;
};

export enum EtaResponseType {
	RouteInfo = 'route_info',
	Task = 'task',
	ActualBreak = 'actual_break',
	PreassignedBreak = 'preassigned_break',
	Directions = 'directions',
	UnassignedTask = 'unassigned_task'
}

export type BaseEtaResponseItem<T> = {
	user_id: number;
	type: T;
	route_idx: number;
	run_id?: number;
};

export type EtaResponseRouteItem = BaseEtaResponseItem<EtaResponseType.RouteInfo> & {
	depot_etl: number; // unix seconds time
	depot_back_eta: number; // unix seconds time
	transport_distance: number;
	transport_time: number;
	run_id: number;
	power_unit_id: number;
};

export type EtaResponseTaskItem = BaseEtaResponseItem<EtaResponseType.Task> & {
	depot_etl: number; // unix seconds time
	eta: number; // unix seconds time
	etl: number; // unix seconds time
	has_to_leave?: number; /// unix seconds time
	priority: number;
	task_id: number;
	type: EtaResponseType.Task;
	user_id: number;
	vehicle_id: string;
	way_point_id: number;
};

export type EtaResponseBreakItem = BaseEtaResponseItem<
	EtaResponseType.ActualBreak | EtaResponseType.PreassignedBreak
> & {
	id?: string;
	position: number;
	scheduled_start_time: number; // unix seconds time
	scheduled_end_time: number; // unix seconds time
	estimated_start_time: number; // unix seconds time
	estimated_end_time: number; // unix seconds time
	start_lat: number;
	start_lng: number;
	end_lat: number;
	end_lng: number;
};

export type RouteGroup = { routes: RunChangedParams[]; minStartTimeMs: number; totalRunIds: number[] };

export type EtaResponseDirectionsItem = BaseEtaResponseItem<EtaResponseType.Directions> & {
	directions: string[];
};

export type EtaResponse = BaseEtaResponseItem<EtaResponseType>[];
export type EtaFetchFunc = (etaRequest: FullEtaRequest) => Promise<EtaResponse>;
export type CalculatePlannedEta = (etaRequest: any) => Promise<any>;

export type RunChangedParams = {
	runOrderedTasks: RoutesPlannerTask[];
	driverBreaks?: RoutesPlannerBreak[];
	runId: number;
	driverId?: number;
	vehicleId?: number;
	trailerId?: number;
	runStartTime?: number;
	runEndTime: number;
	routeIndex: number;
};

export type RoutesPlannerTask = Task & {
	reason?: string;
};

export type RoutesPlannerBreak = Modify<
	EtaResponseBreakItem,
	{
		scheduled_start_time: string;
		scheduled_end_time: string;
		estimated_start_time: string;
		estimated_end_time: string;
	}
> & {
	run_id: number;
};
